import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useNotify, useTranslate } from 'react-admin';
import { extractErrorMessage } from '@/utils/extractErrorMessage';
import {
  GroupsEditorList,
  GroupsEditorToolbar,
} from '@/features/accounts/components/sections/GroupsEditor';
import type { Group } from '@/features/accounts/types/account';
import {
  type AccountOption,
  type ApplicationOption,
  getAccountsList,
  getApplicationsByAccount,
  getGroupById,
  syncGroups,
} from '../services/groupService';

const i18nBase = 'resources.manage-groups';
const LIST_PATH = '/manage-groups';

export const ManageGroupsEditor: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const initialAccountId = searchParams.get('accountId') ?? '';
  const editingGroupId = searchParams.get('groupId');
  const isEditMode = Boolean(editingGroupId);

  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountId, setAccountId] = useState(initialAccountId);

  const [applications, setApplications] = useState<ApplicationOption[]>([]);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [applicationName, setApplicationName] = useState('');
  const [groups, setGroups] = useState<Group[]>([]);
  const [originalGroups, setOriginalGroups] = useState<Group[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [groupsLoaded, setGroupsLoaded] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Load accounts on mount.
  useEffect(() => {
    let cancelled = false;
    setAccountsLoading(true);
    getAccountsList()
      .then((rows) => {
        if (cancelled) return;
        setAccounts(rows);
        if (!isEditMode && rows.length > 0) {
          setAccountId((prev) => prev || rows[0].id);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          notify(
            extractErrorMessage(err, `${i18nBase}.notifications.loadFailed`, translate),
            { type: 'error' },
          );
        }
      })
      .finally(() => {
        if (!cancelled) setAccountsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isEditMode, notify, translate]);

  useEffect(() => {
    if (!accountId) {
      setApplications([]);
      setApplicationName('');
      setGroups([]);
      setOriginalGroups([]);
      setGroupsLoaded(false);
      return;
    }
    let cancelled = false;
    setApplicationsLoading(true);
    getApplicationsByAccount(accountId)
      .then((rows) => {
        if (!cancelled) {
          setApplications(rows);
          setApplicationName((prev) => {
            if (prev && rows.some((r) => r.name === prev)) return prev;
            return !isEditMode && rows.length > 0 ? rows[0].name : '';
          });
        }
      })
      .catch((err) => {
        if (!cancelled) {
          notify(
            extractErrorMessage(err, `${i18nBase}.notifications.loadFailed`, translate),
            { type: 'error' },
          );
        }
      })
      .finally(() => {
        if (!cancelled) setApplicationsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, isEditMode, notify, translate]);

  useEffect(() => {
    if (!isEditMode || !editingGroupId) return;
    let cancelled = false;
    setGroupsLoading(true);
    setGroupsLoaded(false);
    getGroupById(editingGroupId)
      .then((detail) => {
        if (cancelled) return;
        setAccountId(detail.accountId);
        setApplicationName(detail.applicationName);
        const grp: Group = {
          groupId: detail.id,
          groupName: detail.groupName,
          members: detail.members,
        };
        setGroups([grp]);
        setOriginalGroups(JSON.parse(JSON.stringify([grp])) as Group[]);
        setGroupsLoaded(true);
      })
      .catch((err) => {
        if (!cancelled) {
          notify(
            extractErrorMessage(err, `${i18nBase}.notifications.loadFailed`, translate),
            { type: 'error' },
          );
        }
      })
      .finally(() => {
        if (!cancelled) setGroupsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isEditMode, editingGroupId, notify, translate]);

  useEffect(() => {
    if (isEditMode) return;
    setOriginalGroups([]);
    const ready = Boolean(accountId && applicationName);
    setGroupsLoaded(ready);
    setGroups(ready ? [{ groupId: null, groupName: '', members: [] }] : []);
  }, [accountId, applicationName, isEditMode]);

  const handleSubmit = async () => {
    setSubmitAttempted(true);
    if (!accountId) {
      notify(`${i18nBase}.validation.accountRequired`, { type: 'error' });
      return;
    }
    if (!applicationName) {
      notify(`${i18nBase}.validation.applicationRequired`, { type: 'error' });
      return;
    }
    if (groups.length === 0) {
      notify(`${i18nBase}.validation.atLeastOneGroup`, { type: 'error' });
      return;
    }
    if (groups.some((g) => !g.groupName?.trim())) {
      notify(`${i18nBase}.validation.groupNameRequired`, { type: 'error' });
      return;
    }

    setSaving(true);
    try {
      await syncGroups(accountId, applicationName, originalGroups, groups);
      notify(`${i18nBase}.notifications.saved`, { type: 'success' });
      navigate(LIST_PATH);
    } catch (err) {
      notify(
        extractErrorMessage(err, `${i18nBase}.notifications.saveFailed`, translate),
        { type: 'error' },
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    navigate(LIST_PATH);
  };

  const showEditor = Boolean(accountId && applicationName && groupsLoaded);

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          position: 'sticky',
          top: 0,
          zIndex: 2,
          bgcolor: 'background.paper',
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
          <Button
            size="small"
            variant="text"
            startIcon={<ArrowBackIcon fontSize="small" />}
            onClick={() => navigate(LIST_PATH)}
            sx={{ textTransform: 'none' }}
          >
            {translate(`${i18nBase}.actions.back`)}
          </Button>
        </Box>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            mt: 0.5,
            mb: 1,
            ml: 2,
          }}
        >
          {translate(`${i18nBase}.pageTitle`)}
        </Typography>

        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mx: { xs: 1, sm: 2 }, mb: 1 }} elevation={1}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1.5, sm: 2 },
              flexWrap: { xs: 'nowrap', sm: 'wrap' },
              alignItems: 'flex-start',
            }}
          >
            <TextField
              select
              size="small"
              required
              label={translate(`${i18nBase}.fields.account`)}
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              disabled={accountsLoading || isEditMode}
              InputLabelProps={{ shrink: true }}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                minWidth: { xs: 0, sm: 280 },
                flex: { sm: 1 },
              }}
            >
              <MenuItem value="" sx={{ display: 'none' }} />
              {accounts.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              required
              label={translate(`${i18nBase}.fields.application`)}
              value={applicationName}
              onChange={(e) => setApplicationName(e.target.value)}
              disabled={!accountId || applicationsLoading || isEditMode}
              InputLabelProps={{ shrink: true }}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                minWidth: { xs: 0, sm: 280 },
                flex: { sm: 1 },
              }}
              helperText={
                accountId && !applicationsLoading && applications.length === 0
                  ? translate(`${i18nBase}.hints.noApplications`)
                  : ' '
              }
            >
              <MenuItem value="" sx={{ display: 'none' }} />
              {applications.map((a) => (
                <MenuItem key={a.name} value={a.name}>
                  {a.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Paper>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, py: 2 }}>
        {showEditor && (
          <Box sx={{ mx: 2, mb: 2 }}>
            <GroupsEditorToolbar groups={groups} onChange={setGroups} />
          </Box>
        )}
        <Box sx={{ px: 2 }}>
          {accountId && applicationName && groupsLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {showEditor && (
            <GroupsEditorList
              groups={groups}
              onChange={setGroups}
              minGroups={1}
              submitAttempted={submitAttempted}
            />
          )}
        </Box>
      </Box>
      <Box
        sx={{
          flexShrink: 0,
          position: 'sticky',
          bottom: 0,
          zIndex: 2,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          px: { xs: 1, sm: 2 },
          py: 1.5,
          display: 'flex',
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          gap: 1,
          justifyContent: { xs: 'stretch', sm: 'flex-end' },
        }}
      >
        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={handleDiscard}
          disabled={saving}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          {translate(`${i18nBase}.actions.discard`)}
        </Button>
        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={saving || groupsLoading}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          {saving ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            translate(`${i18nBase}.actions.submit`)
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default ManageGroupsEditor;
